import { Form, Formik } from 'formik';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Typography } from '@mui/material';
import { FormPart } from '@modules/shared/components/FormPart';
import { getOptions } from '@shared/commonFunctions';
import { Modal } from '@shared/components/modals/Modal';
import { RadioButtonListField } from '@shared/fields/RadioButtonListField';
import { CancelButton, SaveButton } from '@shared/inputs/Buttons';
import { refreshWorkflow, setLastCommentsRefresh, setRefreshWorkflow } from '@store/reducers/contactManagerSlice';
import { CommonButtonWrapper } from '@styles/CommonStyles';
import { ModalWrapper } from '@styles/StyledDiv';
import { getError } from '@shared/utils/helpers';
import { requestPushApp } from '../../queries';
import { LENDER_CODES } from '../constants';
import { getAssetOnReport } from '../../../Assets/requests';
import { AutoTradeLine, BureauSearchResult, IAssetOnReportsResponse } from '../Tiles/interfaces';
import { FlagshipOverrideModal } from './FlagshipOverrideModal';
import { IAutoLoans, IAutoLoansModalProps } from './interfaces';

export const AutoLoansModal = ({ autoLoans, autoLoansPosition, setOpenAutoLoansModal, data, refreshDealsTable, setLoanErrors }: IAutoLoansModalProps) => {
  const [assetOnReport, setAssetOnReport] = useState<IAssetOnReportsResponse | undefined | null>(null);
  useEffect(() => {
    const fetchAsset = async () => {
      if (data?.assetId) {
        try {
          const result = await getAssetOnReport(data.assetId);
          setAssetOnReport(result);
        } catch (error) {
          console.error('Error fetching asset:', error);
        }
      }
    };
  
    fetchAsset();
  }, [data?.assetId]);
  const dispatch = useDispatch();
  const refreshWorkflowSection = useSelector(refreshWorkflow);

  const [openFlagshipOverride, setOpenFlagshipOverride] = useState(0);
  const [loading, setLoading] = useState(false);

  const lenderCode = data?.lenderResponse.lenderCode?.toUpperCase();
  const isFlagShip = lenderCode === LENDER_CODES.FLAG;

  const updatedOptions = useMemo(() => {
    const updatedAutoLoans = autoLoans.map((loan: IAutoLoans) => (
      {
        ...loan,
        label: loan.lender != 'other'
          ? `${loan.lender}: ${loan.originalAmount}`
          : `${loan.lender} (Enter other Original Loan Amount -> Click on Proceed)`,
        value: `${loan.lender}-${loan.originalAmount}`
      }
    ));
    return updatedAutoLoans;
  }, [autoLoans]);

  function getHighestScoringRecord(searchResults: BureauSearchResult[] | undefined): AutoTradeLine | null {
    let highestScore = -1;
    let highestRecord = null;
    
    searchResults?.forEach(result => {
      if (result.matchingRecords && result.matchingRecords.length > 0) {
        result.matchingRecords.forEach(match => {
          if (match.overallScore > highestScore) {
            highestScore = match.overallScore;
            highestRecord = match.record;
          }
        });
      }
    });
    
    return highestRecord;
  }

  const selectedOption = useMemo(() => {
    if (!assetOnReport?.searchResults) return [];
    
    const highestAssetOnReport = getHighestScoringRecord(assetOnReport.searchResults);
    
    if (!highestAssetOnReport) return [];
    
    const highestAmount = highestAssetOnReport.originalAmount;
    
    const selection = updatedOptions.filter(option => {
      return option.originalAmount === highestAmount;
    });
    
    return selection.length ? selection : [];
  }, [updatedOptions, assetOnReport]);

  const formConfig = useMemo(() => [
    {
      name: 'autoLoan',
      options: getOptions(updatedOptions, 'value', 'label'),
      checked: selectedOption[0]?.value || '',
      component: RadioButtonListField,
      nextLine: true,
    },
  ], [updatedOptions, selectedOption]);

  const handleSubmit = useCallback(async ({ autoLoan }: { autoLoan: string }) => {
    const selectedLoanData = autoLoan.split('-');
    const selectedOption = selectedLoanData[0] !== 'other' && autoLoans.find((loan) => loan.lender === selectedLoanData[0] && loan.originalAmount == +selectedLoanData[1]);
    if (selectedOption ) {
      setLoading(true);
      const payload = {
        dealId: data?.id,
        comment: `Selected Original Loan Amount Value: ${selectedOption.originalAmount}`,
        originalLoanAmount: selectedOption.originalAmount,
        openDate: selectedOption.openDate ?? ''
      };
      const response = lenderCode && await requestPushApp(payload);

      if (!response?.isSuccess && response?.response?.status != 500) {
        const error = getError(response);
        setLoanErrors([error]);
        setOpenAutoLoansModal(0);
        return;
      }

      if (response?.isSuccess) {
        dispatch(setLastCommentsRefresh(new Date().toISOString()));
        dispatch(setRefreshWorkflow(refreshWorkflowSection));
        refreshDealsTable && (await refreshDealsTable());
      } else {
        setLoanErrors(response?.errors ?? []);
      }
      setLoading(false);
      setOpenAutoLoansModal(0);

    } else {
      setOpenFlagshipOverride(prevState => prevState + 1);
    }
  }, [autoLoans, data, lenderCode, isFlagShip, refreshWorkflowSection]);

  return (
    <>
      <Modal
        open
        title="Asset Original Loan Amount Discrepancy"
        dialogWidth="500px"
        resetPosition={autoLoansPosition}
        onClose={() => setOpenAutoLoansModal(0)}
      >
        <ModalWrapper>
          <Typography variant='h4' >Select the correct original loan amount</Typography>
          <Formik
            enableReinitialize
            initialValues={{ autoLoan: '' }}
            onSubmit={handleSubmit}
          >
            {(props) => (
              <Form onSubmit={props.handleSubmit}>
                <FormPart config={formConfig} />
                <CommonButtonWrapper direction="row">
                  <CancelButton onClick={() => setOpenAutoLoansModal(0)} />
                  <SaveButton isLoading={loading} disabled={!props.values.autoLoan} label="Proceed" />
                </CommonButtonWrapper>
              </Form>
            )}
          </Formik>
        </ModalWrapper>
      </Modal>

      {!!openFlagshipOverride &&
        <FlagshipOverrideModal
          flagshipOverrideModalPosition={openFlagshipOverride}
          setOpenFlagshipOverride={setOpenFlagshipOverride}
          setOpenAutoLoansModal={setOpenAutoLoansModal}
          data={data}
          setLoanErrors={setLoanErrors}
        />
      }
    </>
  );
};